import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getStatusBadgeClasses as getSharedStatusBadgeClasses,
  getStatusLabel as getSharedStatusLabel,
} from '@/shared/utils/statusBadge';
import { viewServiceType } from '../api/viewServiceTypeApiService';
import type {
  ServiceType,
  ServiceTypeAuditActor,
} from '../types/serviceTypeTypes';

const SERVICE_TYPE_NAV_ITEMS = [
  {
    id: 'PodstawoweInformacje',
    label: 'Podstawowe informacje',
    dataTestId: 'Podstawowe informacje',
  },
] as const;

export { SERVICE_TYPE_NAV_ITEMS };

const formatDate = (isoString: string | null | undefined): string => {
  if (!isoString) {
    return '—';
  }
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  const formatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  const hour = parts.find((p) => p.type === 'hour')?.value;
  const minute = parts.find((p) => p.type === 'minute')?.value;
  const second = parts.find((p) => p.type === 'second')?.value;
  return `${year}/${month}/${day}, ${hour}:${minute}:${second}`;
};

const orDash = (value: string | null | undefined): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  return value.trim() ? value : '—';
};

const extractActorName = (
  actor: ServiceTypeAuditActor | null | undefined
): string => {
  if (!actor) {
    return '—';
  }
  return orDash(actor.name);
};

export interface UseViewServiceTypeReturn {
  serviceType: ServiceType | undefined;
  isLoading: boolean;
  showErrorScreen: boolean;
  notFound: boolean;
  accessError: boolean;
  statusLabel: string;
  statusBadgeClasses: { container: string; dot: string };
  formattedDescription: string;
  formattedCreatedAt: string;
  formattedUpdatedAt: string;
  formattedUpdatedBy: string;
  refetch: () => Promise<void>;
}

export const useViewServiceType = (
  serviceTypeId: string
): UseViewServiceTypeReturn => {
  const [serviceType, setServiceType] = useState<ServiceType | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(true);
  const [showErrorScreen, setShowErrorScreen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [accessError, setAccessError] = useState(false);

  const fetchServiceType = useCallback(async () => {
    setIsLoading(true);
    setNotFound(false);
    try {
      const response = await viewServiceType(serviceTypeId);
      if (response.success && response.data) {
        setServiceType(response.data);
        setShowErrorScreen(false);
      } else if (response.notFound) {
        setNotFound(true);
        setShowErrorScreen(false);
      } else if (response.forbidden) {
        setAccessError(true);
      } else {
        setShowErrorScreen(true);
      }
    } catch {
      setShowErrorScreen(true);
    } finally {
      setIsLoading(false);
    }
  }, [serviceTypeId]);

  useEffect(() => {
    fetchServiceType();
  }, [fetchServiceType]);

  const statusLabel = useMemo(
    () => getSharedStatusLabel(serviceType?.status),
    [serviceType?.status]
  );

  const statusBadgeClasses = useMemo(
    () => getSharedStatusBadgeClasses(serviceType?.status),
    [serviceType?.status]
  );

  const formattedDescription = useMemo(
    () => orDash(serviceType?.description),
    [serviceType?.description]
  );

  const formattedCreatedAt = useMemo(
    () => formatDate(serviceType?.created_at),
    [serviceType?.created_at]
  );

  const formattedUpdatedAt = useMemo(
    () => formatDate(serviceType?.updated_at),
    [serviceType?.updated_at]
  );

  const formattedUpdatedBy = useMemo(
    () => extractActorName(serviceType?.updated_by),
    [serviceType?.updated_by]
  );

  return {
    serviceType,
    isLoading,
    showErrorScreen,
    notFound,
    accessError,
    statusLabel,
    statusBadgeClasses,
    formattedDescription,
    formattedCreatedAt,
    formattedUpdatedAt,
    formattedUpdatedBy,
    refetch: fetchServiceType,
  };
};
